/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { GridReadyEvent, RowSelectedEvent, ValueGetterParams } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AgFns,
  BaseService,
  GridOptionsExt,
  ISortModel,
  NotificationService,
  ProxAgFns,
  Roles,
  SaveAndQueryComponent,
  StatusChangeDialog,
  StatusService,
} from '@core';
import {
  Account,
  AccountAdmin,
  ActiveStatus,
  ActiveStatusEnum,
  ApprovalTree,
  Program,
  ProgramStartDateTypeEnum,
  ProgramTypeEnum,
  ProximityRightEnum,
} from '@models';

import { UtilFns } from '@utils';

import { AccountDbQueryService } from '../services/account-db-query.service';
import { DialogService } from 'primeng/dynamicdialog';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { AccountActivationService } from '../services/account-activation.service';
import { IProgramNewChoicesParams, ProgramNewChoicesDialogComponent } from './program-new-choices.dialog.component';
import { ProgramNarrativeDialogComponent } from './program-narrative-dialog.component';

@Component({
  selector: 'prox-program-list',
  templateUrl: './program-list.component.html',
  standalone: false,
})
export class ProgramListComponent extends SaveAndQueryComponent {
  accountId!: string;
  account!: Account;
  accountAdmin?: AccountAdmin;
  allPrograms: Program[] = [];
  programs: Program[] = [];
  selectedProgram?: Program;
  progGridOptions: GridOptionsExt = {};
  activeStatuses: ActiveStatus[] = [];
  isGlobalAdmin? = false;
  canAddProgram? = false;
  includeExpiredAndInactive = false;
  anySupplierHasManagedInventory = false;

  /*   editLabel = 'Edit';
  editLabelWidth = 50; */

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    override dbQueryService: AccountDbQueryService,
    override dbSaveService: AccountDbSaveService,
    private statusService: StatusService,
    public pngDialogService: DialogService,
    private accountActivationService: AccountActivationService,
    private notificationService: NotificationService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');

    this.account = await this.dbQueryService.getAccountAndApprovalTreesById(this.accountId);
    this.allPrograms = await this.dbQueryService.getPrograms(this.accountId);

    this.programs = this.applyExpiredAndInactiveFilter();

    this.activeStatuses = await this.dbQueryService.getAll(ActiveStatus);
    this.isGlobalAdmin = this.hasRight(ProximityRightEnum.IsGlobalAdminForAccounts);
    this.canAddProgram = this.hasRight(ProximityRightEnum.CanEditProgram) && !this.isGlobalAdmin;

    // Account-level account-owned inventory: the permission is granted one partnership at a
    // time, but it reads as a capability of the account, so any one partnership carrying it
    // enables the account. Computed outside the canAddProgram branch because the activation
    // gate needs it even for admins who cannot add programs.
    this.anySupplierHasManagedInventory = this.account.supplierAccounts.some((sa) => sa.allowInventory);

    if (this.canAddProgram) {
      UtilFns.assertNonNull(this.authUser?.accountAdminId, 'accountAdminId cannot be null here');
      this.accountAdmin = await this.dbQueryService.getAccountAdminById(this.authUser.accountAdminId);
      // Principal Account Admins see all programs (by role, not by being the account's
      // single primary admin — an account can have multiple PAAs). Others only see
      // programs in their approval trees.
      const isPrincipalAdmin = this.authUser.hasRole(Roles.PrincipalAccountAdmin);
      if (!isPrincipalAdmin) {
        // filter out any programs where the current account admin is not in the approvalTree for the program
        const validApprovalTrees = await this.dbQueryService.getApprovalTreesByAccountAdmin(this.authUser.accountAdminId);
        const approvalTreeIdSet = new Set(validApprovalTrees.map((x) => x.id));
        // Apply approval tree filter to allPrograms so it persists across the expired/inactive toggle
        this.allPrograms = this.allPrograms.filter((p) => p.approvalTreeId == null || approvalTreeIdSet.has(p.approvalTreeId));
        this.programs = this.applyExpiredAndInactiveFilter();
      }
    }

    this.progGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onProgGridReady,
      onRowSelected: this.onProgRowSelected,
    });

    this.accountActivationService.deactivate();

    AgFns.captureGridRouteParams(this.progGridOptions, this.route, 'id');
    this.setTitle('Proximity Programs for ' + this.account.name);
    this.isPageReady = true;
  }

  get title() {
    return 'Account Management - Programs';
  }

  isReadOnly() {
    // superUser must impersonate here.
    return this.statusService.getWorkingStatus(this.account).isReadOnly || this.isGlobalAdmin;
  }

  statusMessage() {
    return this.statusService.getWorkingStatus(this.account as any).statusDisplay;
  }

  onProgGridReady(event: GridReadyEvent) {
    const colDefs = [
      // { ...AgFns.createButtonProps(' ', this.onEdit.bind(this), { label: 'Edit'} ), width: 50 },
      {
        ...AgFns.createButtonProps(' ', this.onEdit.bind(this), {
          label: 'Edit',
        }),
        ...AgFns.fixWidth(60),
        suppressHeaderMenuButton: true,
      },
      {
        ...AgFns.createButtonProps(' ', this.onNarrative.bind(this), {
          label: 'Narrative',
        }),
        ...AgFns.fixWidth(80),
        suppressHeaderMenuButton: true,
      },
      {
        ...AgFns.createButtonProps(' ', this.onInviteAll.bind(this), {
          label: 'Invite All',
          calcDisabled: (row: Program) => row.activeStatusId !== ActiveStatusEnum.Active,
          tooltip:
            'Send invite emails to all active users with access to this program who have not yet confirmed (only available when the program is Active).',
        }),
        ...AgFns.fixWidth(100),
        suppressHeaderMenuButton: true,
      },

      // { headerName: 'Name', field: 'name', filter: 'agTextColumnFilter' },
      {
        headerName: 'Program',
        field: 'name',
        valueGetter: (p: ValueGetterParams) => {
          if (p.data.isExpired) {
            return p.data.name + ' (EXPIRED)';
          }
          return p.data.name;
        },
        filter: 'agTextColumnFilter',
        cellStyle: (p) => {
          if (p.data.isExpired) {
            //mark police cells as red
            return { color: 'red' };
          }
          return null;
        },
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Description',
        field: 'description',
        filter: 'agTextColumnFilter',
        editable: !this.isReadOnly(),
        cellEditor: 'agLargeTextCellEditor',
        cellEditorParams: {
          maxLength: 500,
          rows: 5,
          cols: 50,
        },
        cellEditorPopup: true,
        onCellValueChanged: (params) => this.onDescriptionChanged(params),
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Program Type',
        field: 'programType.name',
        ...AgFns.fixWidth(150),
      },
      {
        headerName: 'Accessibility',
        field: 'programStartDateType.name',
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Start Date',
        field: 'startDate',
        ...AgFns.fixWidth(160),
      },
      {
        headerName: 'End Date',
        field: 'endDate',
        ...AgFns.fixWidth(160),
      },
      // ProxAgFns.getWorkingStatusDef('Working Status', this.statusService),
      {
        ...ProxAgFns.getActiveStatusColDef('activeStatus.name'),
        ...AgFns.fixWidth(100),
        sortable: false,
      },
      ProxAgFns.getEntityStatusColDef(this.onChangeActiveStatus.bind(this)),
    ];

    const sortModel: ISortModel = [
      { colId: 'name', sort: 'asc' },
      { colId: 'startDate', sort: 'asc' },
    ];

    AgFns.initGrid(event, colDefs, sortModel);
  }

  includeExpiredAndInactiveClicked() {
    this.programs = this.applyExpiredAndInactiveFilter();
  }

  applyExpiredAndInactiveFilter() {
    if (this.includeExpiredAndInactive) {
      return this.allPrograms;
    }
    return this.allPrograms.filter((p) => !p.isExpired && p.activeStatusId !== ActiveStatusEnum.Inactive);
  }

  async onProgRowSelected(event: RowSelectedEvent) {
    const program = event.data as Program;
    if (program == null) {
      return;
    }
    if (event.node.isSelected()) {
      this.selectedProgram = program;
      this.updateGridRouteParams(this.progGridOptions, program.id);
    } else {
      if (program == this.selectedProgram) {
        this.selectedProgram = undefined;
      }
    }
  }

  onDeleteProgram(program: Program) {
    // TODO:
  }

  async onDescriptionChanged(params: { data: Program; oldValue: string; newValue: string }) {
    if (params.oldValue === params.newValue) return;

    await this.saveChanges();
    this.toastr.success('Description updated', 'Database Activity');
  }

  // NOTE: this is probably the most baroque status change handler is the app... Ugh...
  async onChangeActiveStatus(program: Program) {
    const valFn = async (selectedStatusId: ActiveStatusEnum, errs: string[]) => {
      if (selectedStatusId == ActiveStatusEnum.Active && !program.approvalTreeId) {
        errs.push('An Proximity Account Organization Tree is required for ACTIVE Programs');
      }

      // Gate 1: an Inventory program runs on account-owned stock, which no supplier has agreed
      // to carry until a partnership enables Account-Owned Inventory Management.
      if (
        selectedStatusId == ActiveStatusEnum.Active &&
        program.programTypeId == ProgramTypeEnum.Inventory &&
        !this.anySupplierHasManagedInventory
      ) {
        errs.push(
          'No Proximity Supplier Partnership for this Account has Account-Owned Inventory Management enabled, ' +
            'so an Inventory Program may not be activated',
        );
      }

      if (
        selectedStatusId == ActiveStatusEnum.Active &&
        (program.programStartDateTypeId == ProgramStartDateTypeEnum.FixedStartAndEndDate ||
          program.programStartDateTypeId == ProgramStartDateTypeEnum.VariableStartFixedEndDate) &&
        program.endDate &&
        program.endDate < new Date()
      ) {
        errs.push('An expired Proximity Program may not be activated');
      }
      return true;
    };
    // onChangeActiveStatus will not set the program's activeStatusId - we will do that below if needed.
    const selectedStatusId = await this.statusService.onChangeActiveStatus(this, program, 'Program', valFn, undefined, false);

    if (selectedStatusId == null) return;

    if (selectedStatusId == ActiveStatusEnum.Active || selectedStatusId == ActiveStatusEnum.Phaseout) {
      // if activating, then we need to navigate to the program form.
      this.router.navigate([program.id], { relativeTo: this.route, queryParams: { activatingStatusCode: selectedStatusId } });
    } else {
      // If not activating, then just change the status.
      program.activeStatusId = selectedStatusId;
      await this.saveChanges();
      this.toastr.success('Status change updated', 'Database Activity');
      this.progGridOptions.apiX?.refreshCells();

      if (selectedStatusId == ActiveStatusEnum.Hold) {
        const programLink = `/account/accounts/${this.accountId}/manage/programs/${program.id}`;
        this.notificationService.add(
          'warn',
          'Program On Hold',
          `<a href="${programLink}">${program.name}</a> is on Hold. Remember to re-activate when editing is complete.`,
          undefined,
          `program-hold:${program.id}`,
        );
      } else {
        // Resolve any existing hold notification for this program
        this.notificationService.resolveByKey(`program-hold:${program.id}`);
      }
    }
  }

  /**
   * Bulk-invite migration tool: queue an invite email to every active user who has access to this
   * program and has not yet confirmed their email. Server re-evaluates eligibility at job time
   * (idempotent re-runs) and emails a completion summary to the triggering admin.
   */
  async onInviteAll(row: Program) {
    if (row.activeStatusId !== ActiveStatusEnum.Active) {
      return;
    }

    let count: number;
    try {
      count = await this.dbSaveService.postPreviewInviteCountForProgram(row.id);
    } catch (err) {
      this.toastr.error('Unable to determine invite count.  See console for details.');
      return;
    }

    if (count === 0) {
      await this.dialogService.okDialog(
        'No Invites to Send',
        `All active users with access to <b>${row.name}</b> have already confirmed their email, or have no email address on file.`,
      );
      return;
    }

    const userWord = count === 1 ? 'user' : 'users';
    const ok = await this.dialogService.askIfOk(
      'Send Invites',
      `Send invite emails to <b>${count}</b> active ${userWord} for program <b>${row.name}</b> who ${count === 1 ? 'has' : 'have'} not yet confirmed their email?<br/><br/>` +
        `Already-confirmed users are skipped automatically.  You will receive an email summary when the batch completes.`,
    );
    if (!ok) {
      return;
    }

    try {
      await this.dbSaveService.postSendInvitesForProgram(row.id);
      this.toastr.success(`Invite batch queued for ${count} ${userWord}.  Check your email for the completion summary.`);
    } catch (err) {
      this.toastr.error('Unable to queue invite batch.  See console for details.');
    }
  }

  async onNarrative(row: Program) {
    const ok = await ProgramNarrativeDialogComponent.openDialog(this.pngDialogService, {
      programId: row.id,
    });

    // this.updateGridRouteParams(this.progGridOptions, row.id);
    // this.router.navigate(['narrative/', row.id], { relativeTo: this.route });
  }

  onEdit(row: Program) {
    /*     if (this.account?.activeStatusId !== ActiveStatusEnum.Active) {
      this.dialogService.okDialog(
        'Cannot Edit Program',
        this.account?.name + " must have a status of 'Active' to edit Account Programs"
      );
      return;
    } */

    this.updateGridRouteParams(this.progGridOptions, row.id);
    this.router.navigate([row.id], { relativeTo: this.route });
  }

  onEditAlt(row: Program) {
    this.updateGridRouteParams(this.progGridOptions, row.id);
    this.router.navigate(['alt/', row.id], { relativeTo: this.route });
  }

  async onAdd() {
    if (<ActiveStatusEnum.Active>this.account?.activeStatusId > ActiveStatusEnum.Hold) {
      this.dialogService.okDialog(
        'Cannot Add Program',
        this.account?.name + " must have a status of 'Active' or 'Hold' to add Account Programs",
      );
      return;
    }

    const hasProgramUserGroups = this.account?.approvalTrees.some((p) => this.approvalTreeHasProgramUserGroups(p));
    if (!hasProgramUserGroups) {
      if (
        await this.dialogService.okDialog(
          'Cannot Add Program',
          'To create a new program, at least one Program User Group must exist within the Proximity Organization',
        )
      ) {
        return;
      }
    }

    const ok = await this.dialogService.askIfOk(
      'Add a New Program',
      'Make sure that a Proximity Account Organization Tree and an Administrator Group are ready for use before creating a new Proximity Program.  <br/>Proceed to Add Program?',
    );
    if (!ok) {
      return;
    }

    const result = await this.dialogService.create<IProgramNewChoicesParams>(ProgramNewChoicesDialogComponent, {
      account: this.account,
      availableAfterJoinDateDays: 0,
      startDate: new Date(),
      endDate: new Date(Date.now() + 364 * 24 * 60 * 60 * 1000),
      durationInDays: 90,
      programType: '',
    });

    if (!result) {
      return;
    }

    this.router.navigate(['add'], {
      relativeTo: this.route,
      queryParams: {
        availableAfterJoinDays: result.availableAfterJoinDateDays,
        startDate: result.startDate,
        endDate: result.endDate,
        durationInDays: result.durationInDays,
        programType: result.programType,
      },
    });
  }

  private approvalTreeHasProgramUserGroups(tree: ApprovalTree) {
    const hasPugs = tree.approvalTreeAdminGroups.some((p) => p.approvalTreeUserGroups.length > 0);
    return hasPugs;
  }
}
